import { useState, useEffect, useRef } from "react";
import { Mail, User, MessageSquare } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import AOS from "aos";
import "aos/dist/aos.css";
import { CheckCircle, Clock, Puzzle } from "lucide-react";
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

const Contact = () => {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [enviado, setEnviado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      setError("Por favor completa el captcha.");
      return;
    }

    setCargando(true);
    setError(null);
    setEnviado(false);
    try {
      const res = await fetch(
        "https://devparts-backend.onrender.com/api/contacto",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nombre,
            correo: email,
            mensaje,
            captcha: captchaToken,
          }),
        }
      );

      if (!res.ok) throw new Error("Error al enviar el formulario");

      setEnviado(true);
      setNombre("");
      setEmail("");
      setMensaje("");
      setCaptchaToken(null);
      setError(null);
      captchaRef.current?.reset();
    } catch {
      setError("Hubo un error. Inténtalo más tarde.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <section className="w-full bg-[#0d1117] py-20 px-6" id="contacto">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
        {/* Lado izquierdo */}
        <div
          // data-aos="fade-right"
          className="flex flex-col justify-center h-full text-white px-2 sm:px-0"
        >
          <h2 className="text-3xl sm:text-5xl font-bold leading-tight mb-6">
            <span className="text-blue-500">Hablemos de tu idea</span>
          </h2>

          <p className="text-neutral-400 text-lg mb-6 max-w-lg">
            Nuestro equipo está listo para ayudarte a construir soluciones
            digitales que realmente encajen contigo.
          </p>

          <div className="space-y-4 text-neutral-300">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-blue-400 mt-1" size={20} />
              <p>Consultoría inicial 100% gratuita</p>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="text-blue-400 mt-1" size={20} />
              <p>Respuesta en menos de 24 horas</p>
            </div>
            <div className="flex items-start gap-3">
              <Puzzle className="text-blue-400 mt-1" size={20} />
              <p>Soluciones a medida, sin plantillas</p>
            </div>
          </div>

          <div className="mt-8 text-sm text-neutral-500 max-w-md">
            ¿No estás seguro por dónde empezar? Cuéntanos tu visión y nosotros
            le damos forma.
          </div>
        </div>

        {/* Formulario */}
        <div
          className="bg-[#161b22] border border-blue-500/20 backdrop-blur-md rounded-2xl p-8 shadow-blue-500/10 shadow-xl"
          // data-aos="fade-left"
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* Nombre */}
            <div>
              <label className="text-sm text-neutral-400 font-medium flex items-center gap-2 mb-1">
                <User size={16} /> Nombre completo
              </label>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                placeholder="Ingresa tu nombre"
                className="w-full bg-[#0d1117] border border-neutral-700 rounded-md px-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-sm text-neutral-400 font-medium flex items-center gap-2 mb-1">
                <Mail size={16} /> Correo electrónico
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="ejemplo@email.com"
                className="w-full bg-[#0d1117] border border-neutral-700 rounded-md px-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
              />
            </div>

            {/* Mensaje */}
            <div>
              <label className="text-sm text-neutral-400 font-medium flex items-center gap-2 mb-1">
                <MessageSquare size={16} /> Tu mensaje
              </label>
              <textarea
                rows={5}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                required
                placeholder="Cuéntanos sobre tu idea o proyecto..."
                className="w-full bg-[#0d1117] border border-neutral-700 rounded-md px-4 py-3 text-white placeholder-neutral-500 resize-none focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
              ></textarea>
            </div>

            <div className="overflow-hidden scale-[0.85] sm:scale-100 origin-top-left">
              <ReCAPTCHA
                sitekey="6LeGQBorAAAAABumEaOmObotKM_RmEiZLTELGy_Q"
                onChange={setCaptchaToken}
                ref={captchaRef}
              />
            </div>


            {/* Botón */}
            {!cargando && (
              <button
                type="submit"
                className="mt-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:to-blue-400 text-white py-3 px-6 rounded-md font-semibold shadow-md hover:scale-105 transition-all w-fit">
                Enviar mensaje
              </button>
            )}

            {/* Estado */}
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            ) : enviado ? (
              <p className="text-green-400 font-medium mt-2">
                ¡Mensaje enviado correctamente!
              </p>
            ) : null}

            {error && <p className="text-red-500 font-medium mt-2">{error}</p>}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
